/**
 * <park-section-card> — an island/section card from the home grid (epic #138,
 * issue #142; see docs/design-system-rebuild-plan.md). Lifted 1:1 from the
 * `.sec-card` markup in components/HomePage.astro.
 *
 * Single source of truth = the pure `renderParkSectionCard()` function below,
 * consumed by BOTH render contexts:
 *   - the live Astro site, via the SSR wrapper `components/ds/ParkSectionCard.astro`,
 *     which calls it server-side and emits the markup inside <park-section-card data-ssr>
 *     so the card is in the initial HTML (SEO / no-JS), and
 *   - the Claude-Design preview, where the custom element renders it client-side.
 * Because both call the SAME function, server and client markup are identical.
 *
 * The PHOTO is slotted (`slot="photo"`): it is a server-rendered EmDash <Image>
 * (with srcset) or a static <img>, so it stays server-rendered and is passed
 * through verbatim rather than reconstructed here.
 *
 * Prices are PRE-FORMATTED strings (formatted server-side via formatEur), passed
 * in as `price-hour` / `price-night`. Emits `.sec-card/.sec-photo/.sec-desc/
 * .sec-cap/.sec-price(--alt)/.sec-muted` (CSS in ./css/park-section-card.css).
 *
 * Attributes:
 *   - title="…"            card heading (h3).
 *   - desc="…"             optional description paragraph (omitted when empty).
 *   - capacity="…"         optional capacity note (omitted when empty).
 *   - price-hour="…"       PRE-FORMATTED hourly price (bold).
 *   - per-hour-label="…"   muted suffix after the hourly price (e.g. "/ hour").
 *   - price-night="…"      optional PRE-FORMATTED nightly price (omitted when empty).
 *   - per-night-label="…"  muted suffix after the nightly price (e.g. "/ night").
 * Slotted content (slot="photo") = the optional photo markup.
 *
 * @dsSpec design-system#FR-3 — the recurring island/price card of the home grid.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const TAG = "park-section-card";

export interface ParkSectionCardProps {
	title?: string;
	desc?: string;
	capacity?: string;
	priceHour?: string;
	perHourLabel?: string;
	priceNight?: string;
	perNightLabel?: string;
}

/**
 * Pure render: (props, photo HTML) → inner HTML string. No DOM access, so it is
 * safe to call server-side (the .astro wrapper) and client-side (the element).
 * `photoHtml` is trusted author/server markup (the slotted <Image>/<img>) and is
 * emitted verbatim. desc/capacity/night-price are omitted when empty, matching
 * the live HomePage conditionals.
 */
export function renderParkSectionCard(
	props: ParkSectionCardProps,
	photoHtml = "",
): string {
	const photo = photoHtml ? photoHtml : "";
	const desc = props.desc
		? html`<p class="sec-desc">${esc(props.desc)}</p>`
		: "";
	const cap = props.capacity
		? html`<p class="sec-cap">${esc(props.capacity)}</p>`
		: "";
	const night = props.priceNight
		? html`<p class="sec-price sec-price--alt">${esc(props.priceNight)}<span class="sec-muted"> ${esc(props.perNightLabel ?? "")}</span></p>`
		: "";
	// An absent title emits NO heading, the same way desc/capacity/night are
	// omitted when empty. A lone card under its own block heading has nothing
	// to add by repeating it, and an empty `<h3>` would still take space and
	// still be announced by assistive tech (anton/forrest#1447, FR-21).
	const heading = props.title ? html`<h3>${esc(props.title)}</h3>` : "";
	return html`<article class="sec-card">${photo}${heading}${desc}${cap}<p class="sec-price"><strong>${esc(props.priceHour ?? "")}</strong><span class="sec-muted"> ${esc(props.perHourLabel ?? "")}</span></p>${night}</article>`;
}

class ParkSectionCard extends ParkElement {
	static get observedAttributes(): string[] {
		return [
			"title",
			"desc",
			"capacity",
			"price-hour",
			"per-hour-label",
			"price-night",
			"per-night-label",
		];
	}

	protected render(): string {
		return renderParkSectionCard(
			{
				title: this.attr("title"),
				desc: this.attr("desc"),
				capacity: this.attr("capacity"),
				priceHour: this.attr("price-hour"),
				perHourLabel: this.attr("per-hour-label"),
				priceNight: this.attr("price-night"),
				perNightLabel: this.attr("per-night-label"),
			},
			this.slotHtml,
		);
	}
}

defineElement(TAG, ParkSectionCard);

export { ParkSectionCard };
